import { useContainerSize } from '@bimser/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import * as React from 'react';
import { Document, PasswordResponses, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import {
	BCButton,
	BCDiagramStateViewer,
	BCForm,
	BCFormItem,
	BCLabel,
	BCLoading,
	BCNotification,
	BCPassword,
	ValidateStatus
} from '../..';
import { findMessage } from '../../BCIntl';
import Styles from '../assets/style.scss';
import { default as IPDFViewerProps } from '../entities/PDFViewer/IPDFViewerProps';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/build/pdf.worker.min.mjs',
	import.meta.url
).toString();

import classNames from 'classnames/bind';
import PDFViewerPage from './PDFViewerPage';

const cx = classNames.bind(Styles);

const options = {
	disableAutoFetch: true,
	disableStream: true
};

const PDFViewer: React.FC<IPDFViewerProps> = props => {
	const [scale, setScale] = React.useState<number>(100);
	const [pdf, setPDF] = React.useState<PDFDocumentProxy>();
	const dimensions = useContainerSize(props.printRef);
	const passwordCallbackRef = React.useRef(null);

	const [passwordRequired, setPasswordRequired] = React.useState(false);
	const [error, setError] = React.useState(null);

	const handlePassword = (callback, reason) => {
		if (
			reason === PasswordResponses.NEED_PASSWORD ||
			reason === PasswordResponses.INCORRECT_PASSWORD
		) {
			setPasswordRequired(true);
			setError(reason === 2 ? findMessage.get('101026') : null);
		}
	};

	const onDocumentLoadSuccess = React.useCallback((args: PDFDocumentProxy) => {
		setPDF(args);
	}, []);

	const onPasswordRequired = React.useCallback(
		(callback: (sesame: string) => void, reason: any) => {
			passwordCallbackRef.current = callback;
			handlePassword(callback, reason);
		},
		[]
	);

	const handleSubmit = async (data: any) => {
		if (data.sesame && passwordCallbackRef.current) {
			passwordCallbackRef.current(data.sesame);
			setPasswordRequired(false);
		}
	};

	const handleSubmitFailed = () => {
		BCNotification.warning({
			description: findMessage.get('103562'),
			message: findMessage.get('100652')
		});
	};

	const renderPasswordForm = () => {
		return (
			<div className={Styles.pdfPasswordForm}>
				<div className={Styles.pdfPasswordFormInner}>
					<BCLabel className={Styles.pdfPasswordLabel}>
						{error || findMessage.get('101025')}
					</BCLabel>
					<BCForm
						autoComplete="off"
						onFinish={handleSubmit}
						onFinishFailed={handleSubmitFailed}
					>
						<BCFormItem
							name="sesame"
							validateStatus={
								error === PasswordResponses.INCORRECT_PASSWORD
									? ValidateStatus.error
									: ValidateStatus.validating
							}
							help={
								error === PasswordResponses.INCORRECT_PASSWORD
									? findMessage.get('101026')
									: null
							}
						>
							<BCPassword />
						</BCFormItem>
						<BCButton
							text={findMessage.get('100414')}
							type="primary"
							htmlType="submit"
						/>
					</BCForm>
				</div>
			</div>
		);
	};

	return (
		<div className={Styles.pdfViewerContainer}>
			<div
				className={cx({
					pdfViewerContent: true,
					hideDocument: passwordRequired
				})}
			>
				{passwordRequired && renderPasswordForm()}
				<Document
					className={Styles.pdfDocument}
					inputRef={props.printRef}
					file={props.data}
					onLoadSuccess={onDocumentLoadSuccess}
					loading={<BCLoading center />}
					noData={findMessage.get('100705')}
					onPassword={onPasswordRequired}
					options={options}
				>
					<PDFViewerPage
						pdf={pdf}
						scale={scale}
						dimensions={dimensions}
					/>
				</Document>
			</div>
			<BCDiagramStateViewer
				maxScale={200}
				minScale={50}
				zoomScaleFactory={10}
				defaultZoomScale={100}
				zoomScale={scale}
				setZoomScale={setScale}
				hideFitScreen
				hideObjectCount
			/>
		</div>
	);
};

export default PDFViewer;
